// src/ProfileAffiliate.jsx
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const API_ORIGIN = process.env.REACT_APP_API_URL;
const API_BASE = `${API_ORIGIN}/api`;

export default function ProfileAffiliate({ username }) {
  const { t: translate, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [error, setError] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [copied, setCopied] = useState(false);

  const todayStr = new Date().toISOString().slice(0, 10); // 'YYYY-MM-DD'
  const [startDate, setStartDate] = useState(todayStr);
  const [endDate, setEndDate] = useState(todayStr);

  const token = localStorage.getItem('token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  // ---- загрузка данных ----
  const fetchData = async () => {
    if (!token) {
      setError(translate('profile.common.authRequired'));
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');

      // профиль
      const profileResp = await axios.get(`${API_BASE}/auth/profile`, { headers });
      const profileData = profileResp.data || {};

      const codeFromProfile =
        profileData.referralCode ||
        profileData.referral_code ||
        profileData.username || // ✅ сначала username
        username; // ✅ запасной вариант

      setReferralCode(String(codeFromProfile || '').trim());

      // сводная инфа + список рефералов
      const summaryResp = await axios.get(`${API_BASE}/affiliate/summary`, { headers });
      const sumData = summaryResp.data || {};
      setSummary(sumData);
      setReferrals(sumData.referredPlayers || []);

      // комиссии с фильтром по датам (полные сутки)
      const params = {};
      if (startDate && endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);
        end.setDate(end.getDate() + 1); // конец интервала = начало следующего дня

        params.start = start.toISOString();
        params.end = end.toISOString();
      }

      const commResp = await axios.get(`${API_BASE}/affiliate/commissions`, {
        headers,
        params,
      });

      const rawCommissions = Array.isArray(commResp.data) ? commResp.data : [];
      console.log('[Affiliate][CLIENT rawCommissions]', rawCommissions);

      // объединяем дубли по одной игре (если оба реферала сыграли)
      const mergedMap = rawCommissions.reduce((acc, item) => {
        const gameKey = item.long_game_id || item.game_id || `no-game-${item.id}`;

        if (!acc[gameKey]) {
          acc[gameKey] = { ...item };
        } else {
          acc[gameKey].commission_amount =
            Number(acc[gameKey].commission_amount || 0) +
            Number(item.commission_amount || 0);

          if (!acc[gameKey].winner && item.winner) {
            acc[gameKey].winner = item.winner;
          }

          const prevDate = new Date(acc[gameKey].created_at || 0);
          const currDate = new Date(item.created_at || 0);
          if (currDate > prevDate) acc[gameKey].created_at = item.created_at;
        }

        return acc;
      }, {});

      const mergedCommissions = Object.values(mergedMap).sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );

      setCommissions(mergedCommissions);
    } catch (e) {
      console.error('[ProfileAffiliate] error:', e);
      setError(translate('profile.affiliate.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username]);

  // Тёмный фон для всей страницы, чтобы не было белых полос
  useEffect(() => {
    const prevBodyBg = document.body.style.backgroundColor;
    const prevHtmlBg = document.documentElement.style.backgroundColor;

    document.body.style.backgroundColor = '#020617';
    document.documentElement.style.backgroundColor = '#020617';

    return () => {
      document.body.style.backgroundColor = prevBodyBg;
      document.documentElement.style.backgroundColor = prevHtmlBg;
    };
  }, []);

  // ---- ссылка ----
  const referralLink = `${window.location.origin}/register?ref=${encodeURIComponent(
    referralCode || username || ''
  )}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error('Clipboard error:', e);
    }
  };

  // ===== стили =====

  // Внешний контейнер — занимает высоту родителя вкладки
  const outerWrapperStyle = {
    background: '#020617',
    height: '100%',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  };

  // Скролл именно внутри партнёрки
  const scrollAreaStyle = {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: '24px 24px 80px',
  };

  // Центровка контента
  const contentStyle = {
    maxWidth: 1200,
    margin: '0 auto',
  };

  const sectionCard = {
    marginBottom: 24,
    padding: 20,
    borderRadius: 12,
    background: 'linear-gradient(135deg, #020617 0%, #020617 40%, #0b1220 100%)',
    border: '1px solid #1f2937',
    boxShadow: '0 18px 45px rgba(0,0,0,0.45)',
  };

  const smallCard = {
    flex: 1,
    minWidth: 180,
    padding: 14,
    borderRadius: 10,
    background: 'rgba(15,23,42,0.9)',
    border: '1px solid #1f2937',
  };

  const labelMuted = { fontSize: 12, color: '#9ca3af', marginBottom: 4 };
  const valueMain = { fontSize: 22, fontWeight: 600, color: '#e5e7eb' };

  // Базовая обёртка для таблиц
  const tableWrapper = {
    overflowX: 'auto',
    overflowY: 'auto',
    maxHeight: 420,
    borderRadius: 10,
    border: '1px solid #1f2937',
    background: '#020617',
  };

  // Разные высоты таблиц
  const referralsTableWrapper = { ...tableWrapper, maxHeight: 260 };
  const gamesTableWrapper = { ...tableWrapper, maxHeight: 620 };

  // Две колонки
  const twoColWrapStyle = {
    display: 'flex',
    gap: 24,
    alignItems: 'flex-start',
    flexWrap: 'wrap',
  };

  const leftColStyle = {
    flex: '2 1 640px',
    minWidth: 520,
  };

  const rightColStyle = {
    flex: '1 1 360px',
    minWidth: 320,
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 13,
  };

  const thStyle = {
    textAlign: 'left',
    padding: '10px 8px',
    color: '#9ca3af',
    fontWeight: 500,
    borderBottom: '1px solid #1f2937',
    background: 'rgba(15,23,42,0.9)',
    position: 'sticky',
    top: 0,
    zIndex: 1,
  };

  const tdStyle = {
    padding: '8px 8px',
    borderBottom: '1px solid #020617',
    color: '#e5e7eb',
  };

  const badgeWinner = {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 999,
    background: 'rgba(34,197,94,0.15)',
    color: '#22c55e',
    fontSize: 12,
  };

  const badgeEmpty = {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 999,
    background: 'rgba(148,163,184,0.15)',
    color: '#9ca3af',
    fontSize: 12,
  };

  const localeByLanguage = {
    ru: 'ru-RU',
    en: 'en-US',
    ka: 'ka-GE',
    hy: 'hy-AM',
    az: 'az-AZ',
    it: 'it-IT',
    fr: 'fr-FR',
  };

  const dateLocale =
    localeByLanguage[i18n.resolvedLanguage || i18n.language] || 'ru-RU';

  // Тип игры
  const getGameType = (c) => {
    if (typeof c.is_long_game !== 'undefined') {
      return c.is_long_game
        ? translate('profile.affiliate.longGame')
        : translate('profile.affiliate.shortGame');
    }

    if (typeof c.isLongGame !== 'undefined') {
      return c.isLongGame
        ? translate('profile.affiliate.longGame')
        : translate('profile.affiliate.shortGame');
    }

    if (c.long_game_id || c.longGameId) {
      return translate('profile.affiliate.longGame');
    }

    if (c.game_type) return c.game_type;
    return translate('profile.affiliate.shortGame');
  };

  return (
    <div style={outerWrapperStyle}>
      <div style={scrollAreaStyle}>
        <div style={contentStyle}>
          {/* Заголовок */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 20,
            }}
          >
            <div>
              <h2 style={{ margin: 0, fontSize: 24, color: '#e5e7eb' }}>
                {translate('profile.affiliate.title')}
              </h2>
              <div style={{ fontSize: 13, color: '#9ca3af', marginTop: 4 }}>
                {translate('profile.affiliate.subtitle')}
              </div>
            </div>
          </div>

          {/* Ошибка / загрузка */}
          {loading && (
            <div style={{ marginBottom: 16, fontSize: 14, color: '#9ca3af' }}>
              {translate('profile.affiliate.loading')}
            </div>
          )}
          {error && !loading && (
            <div
              style={{
                marginBottom: 16,
                padding: 12,
                borderRadius: 8,
                background: 'rgba(239,68,68,0.08)',
                border: '1px solid rgba(239,68,68,0.4)',
                color: '#fecaca',
                fontSize: 13,
              }}
            >
              {error}
            </div>
          )}

          {/* ✅ Верх: две колонки */}
          <div style={twoColWrapStyle}>
            {/* Левая колонка */}
            <div style={leftColStyle}>
              {/* Реферальная ссылка */}
              <div style={sectionCard}>
                <h3 style={{ marginTop: 0, marginBottom: 10, fontSize: 18, color: '#e5e7eb' }}>
                  {translate('profile.affiliate.referralTitle')}
                </h3>
                <p style={{ fontSize: 13, marginBottom: 12, color: '#cbd5f5' }}>
                  {translate('profile.affiliate.referralText')}
                </p>

                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <input
                    type="text"
                    readOnly
                    value={referralLink}
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      borderRadius: 8,
                      border: '1px solid #4b5563',
                      background: '#020617',
                      color: '#e5e7eb',
                      fontSize: 13,
                      outline: 'none',
                    }}
                    onFocus={(e) => e.target.select()}
                  />
                  <button
                    onClick={handleCopy}
                    style={{
                      padding: '9px 16px',
                      borderRadius: 8,
                      border: 'none',
                      background: copied ? '#16a34a' : '#3b82f6',
                      color: '#fff',
                      cursor: 'pointer',
                      fontSize: 13,
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                      transition: 'transform 0.1s ease, box-shadow 0.1s ease',
                      boxShadow: copied ? '0 0 0 rgba(0,0,0,0)' : '0 10px 25px rgba(59,130,246,0.45)',
                    }}
                  >
                    {copied
                      ? translate('profile.common.copied')
                      : translate('profile.common.copy')}
                  </button>
                </div>

                <div style={{ fontSize: 12, marginTop: 8, color: '#9ca3af' }}>
                  {translate('profile.affiliate.yourCode')}:&nbsp;
                  <code
                    style={{
                      padding: '2px 6px',
                      borderRadius: 4,
                      background: '#020617',
                      border: '1px solid #374151',
                      color: '#e5e7eb',
                    }}
                  >
                    {referralCode}
                  </code>
                </div>
              </div>

              {/* Сводка */}
              {!loading && !error && summary && (
                <div style={{ ...sectionCard, paddingTop: 18, paddingBottom: 18 }}>
                  <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                    <div style={smallCard}>
                      <div style={labelMuted}>{translate('profile.affiliate.totalPlayers')}</div>
                      <div style={valueMain}>{summary.totalPlayers ?? 0}</div>
                    </div>

                    <div style={smallCard}>
                      <div style={labelMuted}>{translate('profile.affiliate.referralGames')}</div>
                      <div style={valueMain}>
                        {summary.totalGames ?? (commissions?.length || 0)}
                      </div>
                    </div>

                    <div style={smallCard}>
                      <div style={labelMuted}>{translate('profile.affiliate.commissionEarned')}</div>
                      <div style={{ ...valueMain, color: '#22c55e' }}>
                        {Number(summary.totalCommission || 0).toFixed(2)} $
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Правая колонка */}
            <div style={rightColStyle}>
              {!loading && !error && referrals && referrals.length > 0 && (
                <div style={sectionCard}>
                  <h3 style={{ marginTop: 0, marginBottom: 12, fontSize: 18, color: '#e5e7eb' }}>
                    {translate('profile.affiliate.invitedPlayers')}
                  </h3>

                  <div style={referralsTableWrapper}>
                    <table style={tableStyle}>
                      <thead>
                        <tr>
                          <th style={thStyle}>{translate('profile.affiliate.name')}</th>
                          <th style={thStyle}>{translate('profile.affiliate.registrationDate')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {referrals.map((r) => (
                          <tr key={r.id} style={{ background: 'transparent' }}>
                            <td style={tdStyle}>{r.username}</td>
                            <td style={tdStyle}>
                              {r.createdAt ? new Date(r.createdAt).toLocaleString(dateLocale) : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ✅ Низ: таблица игр на всю ширину */}
          {!loading && !error && (
            <div style={sectionCard}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 10,
                  gap: 12,
                  flexWrap: 'wrap',
                }}
              >
                <h3 style={{ margin: 0, fontSize: 18, color: '#e5e7eb' }}>
                  {translate('profile.affiliate.gameAccruals')}
                </h3>

                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 13 }}>
                  <span style={{ color: '#9ca3af' }}>{translate('profile.affiliate.period')}:</span>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    style={{
                      background: '#020617',
                      border: '1px solid #374151',
                      borderRadius: 6,
                      padding: '4px 8px',
                      color: '#e5e7eb',
                      fontSize: 13,
                    }}
                  />
                  <span style={{ color: '#9ca3af' }}>—</span>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    style={{
                      background: '#020617',
                      border: '1px solid #374151',
                      borderRadius: 6,
                      padding: '4px 8px',
                      color: '#e5e7eb',
                      fontSize: 13,
                    }}
                  />
                  <button
                    onClick={fetchData}
                    style={{
                      padding: '5px 12px',
                      background: '#2563eb',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 6,
                      cursor: 'pointer',
                      fontSize: 13,
                      fontWeight: 500,
                    }}
                  >
                    {translate('profile.affiliate.apply')}
                  </button>
                </div>
              </div>

              {(!commissions || commissions.length === 0) && (
                <div style={{ fontSize: 14, color: '#9ca3af', marginTop: 8 }}>
                  {translate('profile.affiliate.noGames')}
                </div>
              )}

              {commissions && commissions.length > 0 && (
                <div style={gamesTableWrapper}>
                  <table style={tableStyle}>
                    <thead>
                      <tr>
                        <th style={thStyle}>{translate('profile.affiliate.date')}</th>
                        <th style={thStyle}>{translate('profile.affiliate.game')}</th>
                        <th style={thStyle}>{translate('profile.affiliate.players')}</th>
                        <th style={thStyle}>{translate('profile.affiliate.winner')}</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>{translate('profile.affiliate.commission')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {commissions.map((c) => (
                        <tr key={c.id || `${c.game_id}-${c.created_at}`} style={{ background: 'transparent' }}>
                          <td style={tdStyle}>
                            {c.created_at ? new Date(c.created_at).toLocaleString(dateLocale) : '-'}
                          </td>
                          <td style={tdStyle}>{getGameType(c)}</td>
                          <td style={tdStyle}>{c.players || '-'}</td>
                          <td style={tdStyle}>
                            {c.winner ? <span style={badgeWinner}>{c.winner}</span> : <span style={badgeEmpty}>—</span>}
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>
                            {Number(c.commission_amount || 0).toFixed(2)} ₽
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

